/**
 * /assets/categories — the org-configurable asset category editor (ADR-0024
 * Phase 4). Add / rename / retire / delete. Delete is disabled once a category
 * has assets (the FK RESTRICT surfaces as 409) — the retire path is shown
 * instead, mirroring the leave-type editor's hasHistory treatment.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { listCategories, createCategory, updateCategory, deleteCategory, type AssetCategory } from '../api/assets.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';

export const assetCategoriesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/assets/categories',
  component: AssetCategoriesPage,
});

function AssetCategoriesPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['asset', 'categories'], queryFn: listCategories });
  const categories = query.data?.categories ?? [];
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['asset', 'categories'] });

  const create = useMutation({
    mutationFn: () => createCategory({ key: key.trim(), label: label.trim() }),
    onSuccess: () => { setKey(''); setLabel(''); invalidate(); },
  });

  return (
    <section className="p-8 space-y-6 max-w-2xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Asset categories</h1>
        <p className="text-sm text-gray-400">The kinds of asset your organisation issues. Retire a category to stop new use without losing its history.</p>
      </header>

      <form
        className="rounded border border-gray-900 p-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => { e.preventDefault(); if (key.trim() && label.trim()) create.mutate(); }}
      >
        <label className="text-sm text-gray-300 space-y-1"><span>Key</span>
          <input value={key} onChange={(e) => setKey(e.target.value)} aria-label="Category key" placeholder="laptop" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Label</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Category label" placeholder="Laptops" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <button type="submit" disabled={!key.trim() || !label.trim() || create.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">Add category</button>
        <ApiErrorNotice error={create.error} fallback="Could not create category." className="w-full" />
      </form>

      <ul className="space-y-2">
        {categories.map((c) => <CategoryRow key={c.id} category={c} onChanged={invalidate} />)}
      </ul>
    </section>
  );
}

function CategoryRow({ category, onChanged }: { category: AssetCategory; onChanged: () => void }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(category.label);
  const [note, setNote] = useState<string | null>(null);

  const rename = useMutation({ mutationFn: () => updateCategory(category.id, { label: label.trim() }), onSuccess: () => { setEditing(false); setNote(null); onChanged(); } });
  const setActive = useMutation({ mutationFn: (active: boolean) => updateCategory(category.id, { active }), onSuccess: onChanged });
  const remove = useMutation({
    mutationFn: () => deleteCategory(category.id),
    onSuccess: onChanged,
    // FK RESTRICT → 409 when the category still has assets: guide to retire.
    onError: (e) => setNote(e instanceof ApiError && e.status === 409 ? 'This category has assets and can’t be deleted — retire it instead.' : 'Could not delete.'),
  });

  return (
    <li className={`rounded border border-gray-900 px-4 py-3 text-sm ${category.active ? '' : 'opacity-60'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-gray-400 text-xs">{category.key}</span>
          {editing ? (
            <input value={label} onChange={(e) => setLabel(e.target.value)} aria-label={`Rename ${category.key}`} className="rounded border border-gray-700 bg-gray-900 px-2 py-1 text-white text-sm" />
          ) : (
            <span className="text-gray-200">{category.label}</span>
          )}
          {!category.active ? <span className="text-xs text-gray-500">(retired)</span> : null}
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <button type="button" onClick={() => rename.mutate()} className="text-xs text-blue-300 underline">Save</button>
          ) : (
            <button type="button" onClick={() => setEditing(true)} className="text-xs text-gray-400 underline">Rename</button>
          )}
          {category.active ? (
            <button type="button" onClick={() => setActive.mutate(false)} className="text-xs text-yellow-300 underline">Retire</button>
          ) : (
            <button type="button" onClick={() => setActive.mutate(true)} className="text-xs text-green-300 underline">Reinstate</button>
          )}
          <button type="button" onClick={() => remove.mutate()} className="text-xs text-red-400 underline">Delete</button>
        </div>
      </div>
      {note ? <Alert variant="warning" className="mt-2">{note}</Alert> : null}
    </li>
  );
}
